import { useEffect, useRef, useState } from "react";
import Spinner from "@/components/Spinner";
import { X, Loader2, Image as ImageIcon, ScanBarcode, Keyboard, Sparkles } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * BarcodeScanner — capture-only implementation.
 *
 * We deliberately do NOT run a live `getUserMedia` preview because the browser
 * stream can never match the native camera's sharpness/processing. Instead,
 * tapping the primary button opens the phone's real Camera app via
 * `<input capture="environment">`, then we decode the returned still image
 * with `Html5Qrcode.scanFile()`. This works on Android (Chrome/Samsung Internet)
 * and iOS (Safari 14.5+) at full native camera quality.
 *
 * If the camera still fails (worn-out barcodes, glare, cameras that won't
 * focus close enough on some low-end Androids), users can tap "أدخل الرقم يدوياً"
 * and type the barcode themselves. Because that path bypasses image decoding
 * entirely, it works 100% of the time as long as OpenFoodFacts has the product.
 */
export default function PhotoCapture({ onDetected, onClose }) {
  const fileRef = useRef(null);
  const regionRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | decoding | ocr | manual
  const [manualCode, setManualCode] = useState("");

  // Auto-open the native camera as soon as the sheet appears so the user
  // doesn't need an extra tap to start scanning. If they switch to manual
  // entry mode, we do NOT re-open on subsequent renders.
  useEffect(() => {
    if (status !== "idle") return;
    const t = setTimeout(() => fileRef.current?.click(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitManual = () => {
    const raw = manualCode.trim();
    if (!/^\d{8,14}$/.test(raw)) {
      toast.error("الرقم غير صحيح — الباركود بين 8 و 14 رقماً");
      return;
    }
    onDetected?.(raw);
  };

  // Attempt decoding using multiple strategies. Barcode photos are often
  // rotated (users hold phone in landscape for a portrait-oriented label)
  // or lit unevenly — a single-shot decode misses ~40% of real captures.
  // Order:
  //   1) Native `BarcodeDetector` — hardware-accelerated on Chromium
  //      Android; handles EAN-13/UPC-A/Code-128 out of the box.
  //   2) `Html5Qrcode.scanFile()` with 4 rotations (0/90/180/270).
  const decodeImage = async (file) => {
    // Strategy 1 — native BarcodeDetector (Chromium, Samsung, Edge, Opera).
    if (typeof window !== "undefined" && "BarcodeDetector" in window) {
      try {
        const supported = await window.BarcodeDetector.getSupportedFormats?.();
        // Feed common product barcode formats explicitly so the detector
        // doesn't waste cycles on QR/aztec/pdf417.
        const wanted = ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "itf", "codabar"]
          .filter((f) => !supported || supported.includes(f));
        const detector = new window.BarcodeDetector({ formats: wanted.length ? wanted : undefined });
        const bmp = await createImageBitmap(file);
        for (const angle of [0, 90, 180, 270]) {
          const canvas = document.createElement("canvas");
          if (angle === 0 || angle === 180) { canvas.width = bmp.width;  canvas.height = bmp.height; }
          else                                { canvas.width = bmp.height; canvas.height = bmp.width; }
          const ctx = canvas.getContext("2d");
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.rotate((angle * Math.PI) / 180);
          ctx.drawImage(bmp, -bmp.width / 2, -bmp.height / 2);
          const found = await detector.detect(canvas);
          const raw = found?.[0]?.rawValue;
          if (raw && /^\d{8,14}$/.test(raw)) return raw;
        }
      } catch {
        // Fall through to Html5Qrcode
      }
    }

    // Strategy 2 — Html5Qrcode.scanFile with 4 rotations.
    const tmp = new Html5Qrcode(regionRef.current.id, {
      verbose: false,
      experimentalFeatures: { useBarCodeDetectorIfSupported: true },
    });
    try {
      const bmp = await createImageBitmap(file);
      for (const angle of [0, 90, 180, 270]) {
        try {
          const canvas = document.createElement("canvas");
          if (angle === 0 || angle === 180) { canvas.width = bmp.width;  canvas.height = bmp.height; }
          else                                { canvas.width = bmp.height; canvas.height = bmp.width; }
          const ctx = canvas.getContext("2d");
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.rotate((angle * Math.PI) / 180);
          ctx.drawImage(bmp, -bmp.width / 2, -bmp.height / 2);
          const rotatedBlob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.95));
          const rotatedFile = new File([rotatedBlob], "rotated.jpg", { type: "image/jpeg" });
          const decoded = await tmp.scanFile(rotatedFile, false);
          if (/^\d{8,14}$/.test(decoded)) return decoded;
        } catch { /* try next angle */ }
      }
    } finally {
      try { await tmp.clear(); } catch {}
    }
    return null;
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setStatus("decoding");
    try {
      const decoded = await decodeImage(file);
      if (decoded) {
        onDetected?.(decoded);
        return;
      }
      // ─────────── AI OCR fallback ───────────
      // The bar-decoders failed (dented pack, glare, camera couldn't focus…).
      // Product barcodes always print the numeric value under the bars, so
      // send the same photo to the vision model and let it OCR the digits.
      // If OFF has the product, the server returns the whole food payload
      // in the same round-trip so we can drop the user straight into the
      // review screen — no extra taps needed.
      setStatus("ocr");
      try {
        const b64 = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(String(r.result).split(",")[1]);
          r.onerror = () => rej(new Error("read fail"));
          r.readAsDataURL(file);
        });
        const { data } = await api.post("/foods/barcode/ocr", { image_base64: b64 });
        if (data?.barcode && /^\d{8,14}$/.test(data.barcode)) {
          toast.success("تم قراءة الأرقام بالذكاء الاصطناعي ✨");
          // Pass the resolved food along if OFF matched — the parent
          // handler can accept either signature (code) or (code, food).
          onDetected?.(data.barcode, data.food || null);
          return;
        }
      } catch (err) {
        // OCR call itself errored (network, rate-limit) — fall through to
        // the standard "please retry / manual entry" UX below.
      }
      toast.error("لم يتم قراءة الباركود — قرّب الكاميرا وتأكد من وضوح الأرقام");
      setStatus("idle");
    } catch {
      toast.error("تعذّرت قراءة الصورة — حاول مرة أخرى");
      setStatus("idle");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-neutral-900 flex flex-col text-white" dir="rtl" data-testid="barcode-scanner">
      {/* Hidden decoding region used by Html5Qrcode.scanFile — must exist in the DOM */}
      <div ref={regionRef} id="barcode-scanner-region" className="hidden" />

      <header className="p-4 flex items-center justify-between">
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center"
          data-testid="barcode-close-btn"
          aria-label="إغلاق"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="text-sm font-semibold">مسح باركود</div>
        <div className="w-10" />
      </header>

      <div className="flex-1 flex flex-col items-center justify-center px-8 gap-6 text-center">
        <div className="w-24 h-24 rounded-3xl bg-white/10 flex items-center justify-center">
          {status === "decoding" || status === "ocr" ? (
            <Spinner size="lg" bare />
          ) : status === "manual" ? (
            <Keyboard className="w-14 h-14 text-white" strokeWidth={1.6} />
          ) : (
            <ScanBarcode className="w-14 h-14 text-white" strokeWidth={1.6} />
          )}
        </div>

        {status === "ocr" ? (
          <div className="max-w-xs">
            <div className="text-lg font-bold mb-2 flex items-center justify-center gap-2">
              <Sparkles className="w-5 h-5 text-brand-300" />
              الذكاء الاصطناعي يقرأ الأرقام...
            </div>
            <p className="text-sm text-white/70 leading-relaxed">
              الباركود غير واضح — نقرأ الأرقام المطبوعة تحته بالذكاء الاصطناعي.
            </p>
          </div>
        ) : status === "manual" ? (
          <div className="w-full max-w-xs space-y-4">
            <div>
              <div className="text-lg font-bold mb-2">أدخل رقم الباركود</div>
              <p className="text-sm text-white/70 leading-relaxed">
                اكتب الأرقام الموجودة أسفل الباركود على العبوة —
                <br />
                عادةً 8 إلى 13 رقماً.
              </p>
            </div>
            <input
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              autoFocus
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value.replace(/\D/g, "").slice(0, 14))}
              onKeyDown={(e) => { if (e.key === "Enter") submitManual(); }}
              placeholder="مثال: 6221033000100"
              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-center text-lg tracking-widest text-white placeholder:text-white/40 focus:outline-none focus:border-white/60"
              data-testid="barcode-manual-input"
              dir="ltr"
            />
          </div>
        ) : (
          <div>
            <div className="text-lg font-bold mb-2">امسح باركود المنتج</div>
            <p className="text-sm text-white/70 leading-relaxed">
              سنفتح كاميرا جوالك الأصلية بجودتها الكاملة —
              <br />
              وجّه الكاميرا نحو الباركود والتقط الصورة، وسنقرأه لك تلقائياً.
            </p>
          </div>
        )}
      </div>

      <footer className="p-6 space-y-3">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFile}
          onCancel={() => { if (status === "idle") onClose?.(); }}
          className="hidden"
          data-testid="barcode-native-capture-input"
        />
        {status === "manual" ? (
          <>
            <button
              type="button"
              onClick={submitManual}
              disabled={!/^\d{8,14}$/.test(manualCode.trim())}
              className="w-full orange-gradient text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-40 shadow-brand"
              data-testid="barcode-manual-submit-btn"
            >
              بحث عن المنتج
            </button>
            <button
              type="button"
              onClick={() => { setStatus("idle"); setTimeout(() => fileRef.current?.click(), 100); }}
              className="w-full text-white/70 py-2 text-sm flex items-center justify-center gap-2"
              data-testid="barcode-back-to-camera-btn"
            >
              <ImageIcon className="w-4 h-4" /> عودة للكاميرا
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={status === "decoding" || status === "ocr"}
              className="w-full orange-gradient text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-60 shadow-brand"
              data-testid="barcode-native-capture-btn"
            >
              {status === "decoding" ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> جارٍ قراءة الصورة...</>
              ) : status === "ocr" ? (
                <><Sparkles className="w-5 h-5" /> يقرأ الأرقام بالذكاء الاصطناعي...</>
              ) : (
                <><ImageIcon className="w-5 h-5" /> افتح الكاميرا والتقط صورة</>
              )}
            </button>
            <button
              type="button"
              onClick={() => setStatus("manual")}
              disabled={status === "decoding" || status === "ocr"}
              className="w-full text-white/80 py-2 text-sm flex items-center justify-center gap-2 disabled:opacity-40"
              data-testid="barcode-switch-to-manual-btn"
            >
              <Keyboard className="w-4 h-4" /> الكاميرا لا تعمل؟ أدخل الرقم يدوياً
            </button>
          </>
        )}
        <button
          type="button"
          onClick={onClose}
          className="w-full text-white/50 py-1 text-xs"
          data-testid="barcode-cancel-btn"
        >
          إلغاء
        </button>
      </footer>
    </div>
  );
}
